
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Save, Check, AlertCircle } from 'lucide-react';

interface AutosaveIndicatorProps {
  lastSaved: Date | null;
  className?: string;
}

export const AutosaveIndicator = ({ lastSaved, className }: AutosaveIndicatorProps) => {
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [initialRender, setInitialRender] = useState(true);
  
  // Reset all states when component mounts
  useEffect(() => {
    setSaving(false);
    setShowSaved(false);
    setSaveError(false);
    
    // Set initial render flag to true and reset after a delay
    setInitialRender(true);
    const timer = setTimeout(() => {
      setInitialRender(false);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Show "Saving..." indicator when a save is in progress
  useEffect(() => {
    if (lastSaved) {
      // Reset error state when lastSaved changes
      setSaveError(false);
      setSaving(true);
      
      const timer = setTimeout(() => {
        setSaving(false);
        setShowSaved(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [lastSaved]);
  
  // Hide the "Saved!" indicator after 3 seconds
  useEffect(() => {
    if (showSaved) {
      const timer = setTimeout(() => {
        setShowSaved(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [showSaved]);
  
  // Improved handling of save errors - only show after medium delay with no save activity
  useEffect(() => {
    // Skip error display during initial render
    if (initialRender) {
      return;
    }
    
    // Reset error state when lastSaved changes
    if (lastSaved) {
      setSaveError(false);
      return;
    }
    
    // Only set error after 60 seconds of no activity
    const errorTimer = setTimeout(() => {
      // If we haven't had a successful save in a while and we're not in the process of saving
      if (!saving && !showSaved && !lastSaved) {
        setSaveError(true);
      }
    }, 60000); // Wait 60 seconds before showing any error (reduced from 120 seconds)
    
    return () => {
      clearTimeout(errorTimer);
    };
  }, [lastSaved, saving, showSaved, initialRender]);
  
  if (saving) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-sm font-medium",
        className
      )}>
        <Save className="h-3.5 w-3.5 animate-pulse text-amber-500" />
        <span className="text-amber-700">Saving...</span>
      </div>
    );
  }
  
  if (showSaved) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-sm font-medium",
        className
      )}>
        <Check className="h-3.5 w-3.5 text-green-500" />
        <span className="text-green-700">Saved!</span>
      </div>
    );
  }
  
  if (saveError && !initialRender) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-sm font-medium",
        className
      )}>
        <AlertCircle className="h-3.5 w-3.5 text-red-500" />
        <span className="text-red-700">Save status unknown</span>
      </div>
    );
  }
  
  if (lastSaved) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border text-sm font-medium",
        className
      )}>
        <span className="text-muted-foreground">
          Last saved: {lastSaved.toLocaleTimeString()}
        </span>
      </div>
    );
  }
  
  return null;
};
